import React, { useState } from "react";
import { Button, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { BadgeStatus, UserBadge } from "../types/Types";
import { getBadgeIcon } from "../util/BadgeUtil";
import { httpsCallable } from "@firebase/functions";
import { functions } from "../../firebase/FirebaseProvider";

interface Props {
    badges: Array<UserBadge>;
}

function UserBadgesCard(props: Props) {
    const [loadingBadgeId, setLoadingBadgeId] = useState<string | undefined>(undefined);
    const [reorderingId, setReorderingId] = useState<string | undefined>(undefined);

    const sortedBadges = [...props.badges].sort((a, b) => a.order - b.order);

    if (sortedBadges.length === 0) {
        return null;
    }

    const toggleBadgeStatus = async (badge: UserBadge) => {
        if (!badge.id) return;
        setLoadingBadgeId(badge.id);
        try {
            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const newStatus = badge.status === BadgeStatus.ACTIVE ? BadgeStatus.INACTIVE : BadgeStatus.ACTIVE;
            await addUserBadge({
                badgeId: badge.id,
                badgeType: badge.badgeType,
                description: badge.description,
                order: badge.order,
                status: newStatus
            });
        } catch (error) {
            console.error('Error toggling badge status:', error);
        } finally {
            setLoadingBadgeId(undefined);
        }
    };

    const moveBadge = async (index: number, direction: 'up' | 'down') => {
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= sortedBadges.length) return;

        const badgeA = sortedBadges[index];
        const badgeB = sortedBadges[swapIndex];
        if (!badgeA.id || !badgeB.id) return;

        setReorderingId(badgeA.id);
        try {
            const addUserBadge = httpsCallable(functions, 'addUserBadge');
            const orderA = badgeA.order;
            const orderB = badgeB.order;
            await Promise.all([
                addUserBadge({
                    badgeId: badgeA.id,
                    badgeType: badgeA.badgeType,
                    description: badgeA.description,
                    order: orderB,
                    status: badgeA.status
                }),
                addUserBadge({
                    badgeId: badgeB.id,
                    badgeType: badgeB.badgeType,
                    description: badgeB.description,
                    order: orderA,
                    status: badgeB.status
                })
            ]);
        } catch (error) {
            console.error('Error reordering badges:', error);
        } finally {
            setReorderingId(undefined);
        }
    };

    const isAnyLoading = loadingBadgeId !== undefined || reorderingId !== undefined;

    return (
        <Card className={"mb-3"}>
            <Card.Header>
                <div>User Badges</div>
            </Card.Header>
            <Card.Body className={"p-0"}>
                {sortedBadges.map((badge, index) => (
                    <div
                        key={badge.id ?? index}
                        className={`d-flex align-items-center p-3 ${index < sortedBadges.length - 1 ? "border-bottom" : ""}`}
                    >
                        <div className={"d-flex flex-column mr-3"} style={{ gap: '2px' }}>
                            <Button
                                variant="link"
                                size="sm"
                                className={"p-0 text-muted"}
                                style={{ lineHeight: 1, minWidth: '1.5rem' }}
                                disabled={index === 0 || isAnyLoading}
                                onClick={() => moveBadge(index, 'up')}
                                aria-label="Move up"
                            >
                                <FontAwesomeIcon icon={faChevronUp} />
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                className={"p-0 text-muted"}
                                style={{ lineHeight: 1, minWidth: '1.5rem' }}
                                disabled={index === sortedBadges.length - 1 || isAnyLoading}
                                onClick={() => moveBadge(index, 'down')}
                                aria-label="Move down"
                            >
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </div>
                        <FontAwesomeIcon
                            icon={getBadgeIcon(badge.badgeType)}
                            className={`mr-3 ${badge.status === BadgeStatus.ACTIVE ? "text-white" : "text-muted"}`}
                            fixedWidth
                        />
                        <span className={badge.status !== BadgeStatus.ACTIVE ? "text-muted" : ""}>
                            {badge.description}
                        </span>
                        <div className={"ml-auto"}>
                            {badge.status === BadgeStatus.ACTIVE ? (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={isAnyLoading}
                                    onClick={() => toggleBadgeStatus(badge)}
                                >
                                    {loadingBadgeId === badge.id ? "Deactivating..." : "Deactivate"}
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={isAnyLoading}
                                    onClick={() => toggleBadgeStatus(badge)}
                                >
                                    {loadingBadgeId === badge.id ? "Activating..." : "Activate"}
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </Card.Body>
        </Card>
    );
}

export default UserBadgesCard;
